/**
 * GET 請求去重（in-flight deduplication）
 *
 * 問題：同一個畫面上多個元件各自 useEffect 打同一支 API，
 *      例如 /appointments 進場時 bookings/month、shop-orders/stats、
 *      employees dayoff 都被打了兩次，時間戳完全相同。
 *
 * 作法：第一支請求還在路上時，後來的相同請求不再送出，直接共用同一個 Promise。
 *
 * 為什麼安全：
 *   - 只處理 GET，寫入請求（POST/PUT/PATCH/DELETE）完全不碰。
 *   - 不做任何快取。請求一結束就把紀錄刪掉，下次呼叫是全新的請求，
 *     所以不會有「改完資料卻讀到舊值」的問題。
 *   - 每個呼叫端拿到自己的 response 複本，互相修改 data 不會污染彼此。
 *
 * 要跳過去重（例如輪詢），在 config 加上 dedupe: false：
 *   api.get('/api/xxx', { dedupe: false })
 */

/** key -> Promise，只存「還在路上」的請求 */
const inflight = new Map()

/** 讓每個呼叫端拿到獨立的 data，避免共用同一個物件被互相改壞 */
function cloneResponse (res) {
  try {
    if (typeof structuredClone === 'function') {
      return { ...res, data: structuredClone(res.data) }
    }
    return { ...res, data: JSON.parse(JSON.stringify(res.data)) }
  } catch (e) {
    // 無法複製（含不可序列化的值）就退回共用同一份
    return res
  }
}

function buildKey (url, config, scope) {
  let params = ''
  try {
    params = JSON.stringify(config?.params ?? null)
  } catch (e) {
    // 參數無法序列化就視為不可去重
    return null
  }
  return `${scope || ''}|${url}|${params}`
}

/**
 * 包裝 axios 實例的 get，加上 in-flight 去重。
 *
 * @param {import('axios').AxiosInstance} instance
 * @param {() => string|null} getScope 取得目前分店識別（不同店不可共用結果）
 * @returns {import('axios').AxiosInstance} 同一個 instance（原地改寫 get）
 */
export function withGetDedupe (instance, getScope) {
  const originalGet = instance.get.bind(instance)

  instance.get = function dedupedGet (url, config) {
    if (config && config.dedupe === false) {
      return originalGet(url, config)
    }

    const key = buildKey(url, config, typeof getScope === 'function' ? getScope() : null)
    if (!key) return originalGet(url, config)

    const existing = inflight.get(key)
    if (existing) {
      return existing.then(cloneResponse)
    }

    const p = originalGet(url, config)
    inflight.set(key, p)

    // 成功或失敗都要清掉，否則下一次呼叫會拿到已結束的舊 Promise
    p.then(
      () => inflight.delete(key),
      () => inflight.delete(key)
    )

    return p
  }

  return instance
}

/** 清空所有進行中的紀錄（切換店家、登出時呼叫，避免跨店共用） */
export function clearInflight () {
  inflight.clear()
}

export default withGetDedupe
